import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Eye, FileText, Trash2, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import PaymentReceipt from '../../PaymentReceipt';
import EmptyState from '@/components/ui/EmptyState';

const PaymentsTab = ({ payments, members, fetchData }) => {
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [diagnosticsDialog, setDiagnosticsDialog] = useState(false);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);

  const handleViewReceipt = async (paymentId) => {
    setLoadingReceipt(true);
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt`);
      setSelectedReceipt(response.data);
      setReceiptDialog(true);
    } catch (error) {
      toast.error('Failed to generate receipt');
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleDeleteClick = (payment) => {
    setSelectedPayment(payment);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPayment) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/payments/${selectedPayment.id}`);
      toast.success('Payment deleted successfully');
      setDeleteDialog(false);
      setSelectedPayment(null);
      if (fetchData) fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewDiagnostics = async () => {
    setLoadingDiagnostics(true);
    setDiagnosticsDialog(true);
    try {
      const response = await axios.get(`${API}/payments/diagnostics/monthly-breakdown`);
      setDiagnosticsData(response.data);
    } catch (error) {
      toast.error('Failed to load diagnostics');
      setDiagnosticsDialog(false);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    setCleaningDuplicates(true);
    try {
      const response = await axios.delete(`${API}/payments/diagnostics/cleanup-duplicates`);
      toast.success(response.data.message);
      setDiagnosticsDialog(false);
      if (fetchData) fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup duplicates');
    } finally {
      setCleaningDuplicates(false);
    }
  };

  return (
    <div data-testid="payments-tab">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
        <Button 
          variant="outline" 
          onClick={handleViewDiagnostics}
          data-testid="diagnostics-btn"
        >
          <Search className="h-4 w-4 mr-2" />
          Revenue Diagnostics
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>View all payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <EmptyState
              icon="dollar"
              title="No Payments Yet"
              description="Payment transactions will appear here once members make payments for memberships and services."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="font-semibold text-gray-900">${payment.amount.toFixed(2)}</TableCell>
                    <TableCell className="capitalize">{payment.payment_method.replace('_', ' ')}</TableCell>
                    <TableCell>
                      <Badge variant={payment.payment_status === 'completed' ? 'default' : payment.payment_status === 'pending' ? 'secondary' : 'destructive'}>
                        {payment.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-700">{payment.description}</TableCell>
                    <TableCell className="text-sm text-gray-600">{payment.reference_number || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {payment.payment_status === 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewReceipt(payment.id)}
                            data-testid={`view-receipt-${payment.id}`}
                            className="hover:bg-teal-50"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Receipt
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteClick(payment)}
                          data-testid={`delete-payment-${payment.id}`}
                          className="hover:bg-red-50 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Receipt</DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <PaymentReceipt receipt={selectedReceipt} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Payment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment record?
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="bg-gray-50 p-4 rounded-lg my-4">
              <p><strong>Amount:</strong> ${selectedPayment.amount?.toFixed(2)}</p>
              <p><strong>Description:</strong> {selectedPayment.description}</p>
              <p><strong>Date:</strong> {new Date(selectedPayment.created_at).toLocaleDateString()}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diagnostics Dialog */}
      <Dialog open={diagnosticsDialog} onOpenChange={setDiagnosticsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Revenue Diagnostics</DialogTitle>
            <DialogDescription>
              View all payments contributing to monthly revenue
            </DialogDescription>
          </DialogHeader>
          
          {loadingDiagnostics ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2">Loading diagnostics...</span>
            </div>
          ) : diagnosticsData ? (
            <div>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Month</p>
                    <p className="text-lg font-semibold">{diagnosticsData.month}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-lg font-semibold text-green-600">${diagnosticsData.total_revenue?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Count</p>
                    <p className="text-lg font-semibold">{diagnosticsData.payment_count}</p>
                  </div>
                </div>
              </div>

              {diagnosticsData.payments?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Stripe Session</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diagnosticsData.payments.map((payment, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{new Date(payment.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-semibold">${payment.amount?.toFixed(2)}</TableCell>
                        <TableCell>{payment.payment_type}</TableCell>
                        <TableCell>{payment.description}</TableCell>
                        <TableCell className="text-xs text-gray-500 max-w-[150px] truncate">
                          {payment.stripe_payment_intent_id}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-gray-500 py-4">No payments this month</p>
              )}

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>Tip:</strong> If you see duplicate payments (same Stripe session), click below to clean them up automatically.
                </p>
                <Button 
                  variant="outline"
                  onClick={handleCleanupDuplicates}
                  disabled={cleaningDuplicates}
                  className="w-full"
                >
                  {cleaningDuplicates ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Cleaning...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clean Up Duplicate Payments
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default PaymentsTab;
